import { RasaContext } from 'context'
import * as React from 'react'
import { Button, Input, Card, CardBody, Nav, NavItem, NavLink, Row, Col, FormGroup, Label, Alert } from 'reactstrap'
import { HeaderComponent } from 'components/header/component'
import { Loading } from 'components/loading'
import { AjaxWrapper, HttpMethod } from 'generic/ajaxWrapper'
import { tryParseJSON } from 'generic/utility'
import { ImageSelectorModal } from 'components/common/image-selector/modal'
import * as Flash from 'components/flash'
import './_background-discovery.scss'

interface DiscoveryData {
  image: string
  colors: {
    primary: string,
    secondary: string,
  }
  style: string
  summary: string
  users: string[]
  url: string
  title: string
}

interface DiscoveryTab {
  id: string
  title: string
  url: string
  data?: DiscoveryData
  rawData?: {
    account: any,
    puppeteer: any,
    jina: any,
  }
  isLoading: boolean
  hasUnsavedChanges: boolean
}

interface State {
  tabs: DiscoveryTab[]
  activeTabId: string
  isDiscovering: boolean
  identifier: string
  accountId: string
  error: string
  showAdvancedData: boolean
  openRawDataCard: string
  showImageSelectorModal: boolean
}

const EMPTY_STATE: State = {
  tabs: [],
  activeTabId: '',
  isDiscovering: false,
  identifier: '',
  accountId: '',
  error: '',
  showAdvancedData: false,
  openRawDataCard: '',
  showImageSelectorModal: false
}

export class BackgroundDiscoveryComponent extends React.Component<any, State> {
  public static contextType = RasaContext

  constructor(props: any) {
    super(props)
    this.state = EMPTY_STATE
  }

  public componentDidMount = () => {
    this.context.user.init().then(({person, activeCommunity}) => {
      const accountData = activeCommunity.billingInfo.productSubscription
      this.setState({
        accountId: accountData.account_id,
        identifier: activeCommunity.communityId,
      }, () => {
        this.loadExistingDiscoveries()
        
        // Auto-invoke discovery for new Enterprise accounts
        if (this.isNewEnterpriseAccount(accountData)) {
          this.createNewTab(accountData.account_website || '', true)
        }
      })
    })
  }

  private isNewEnterpriseAccount = (accountData: any): boolean => {
    // Check if this is a new Enterprise account that needs auto-discovery
    return accountData.account_website && !this.hasExistingDiscoveries()
  }

  private hasExistingDiscoveries = (): boolean => {
    return this.state.tabs.length > 0
  }

  private loadExistingDiscoveries = () => {
    // Load existing discoveries from entity metadata
    this.context.entityMetadata.getEntityObject('accountDiscovery')
      .then((entityObject) => {
        entityObject.load(this.state.identifier, this.state.accountId)
          .then(() => {
            if (entityObject.data && entityObject.data.id) {
              // Parse JSON fields using tryParseJSON utility
              const parsedColors = tryParseJSON(entityObject.data.colors || '{}')
              const parsedContent = typeof entityObject.data.content === 'string' 
                ? tryParseJSON(entityObject.data.content || '{}')
                : entityObject.data.content || {}
              
              const websiteUrl = parsedContent.url || entityObject.data.website || entityObject.data.url || ''
              const tabTitle = websiteUrl ? this.extractDomainFromUrl(websiteUrl) : 'Existing Discovery'
              const existingTab: DiscoveryTab = {
                id: entityObject.data.id,
                title: tabTitle,
                url: websiteUrl,
                data: {
                  image: entityObject.data.image || '',
                  colors: {
                    primary: parsedColors.primary || '#000000',
                    secondary: parsedColors.secondary || '#ffffff'
                  },
                  style: parsedContent.style || '',
                  summary: parsedContent.summary || entityObject.data.summary || '',
                  users: parsedContent.users || [],
                  url: websiteUrl,
                  title: parsedContent.title || entityObject.data.title || this.extractDomainFromUrl(websiteUrl)
                },
                isLoading: false,
                hasUnsavedChanges: false
              }
              this.setState({
                tabs: [existingTab],
                activeTabId: existingTab.id
              })
            } else {
              // No existing discoveries, create a default tab
              this.createDefaultTab()
            }
          })
          .catch(() => {
            // Error loading, create a default tab
            this.createDefaultTab()
          })
      })
      .catch(() => {
        // Error getting entity, create a default tab
        this.createDefaultTab()
      })
  }

  private createDefaultTab = () => {
    if (this.state.tabs.length === 0) {
      this.createNewTab()
    }
  }

  private extractDomainFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`)
      return urlObj.hostname.replace('www.', '')
    } catch {
      return url.substring(0, 20) + (url.length > 20 ? '...' : '')
    }
  }

  private createNewTab = (url: string = '', autoDiscover: boolean = false) => {
    const newTab: DiscoveryTab = {
      id: `tab_${Date.now()}`,
      title: url ? this.extractDomainFromUrl(url) : 'New Discovery',
      url,
      data: null,
      isLoading: autoDiscover,
      hasUnsavedChanges: url ? true : false // Mark as having changes if URL is provided
    }

    this.setState(prevState => ({
      tabs: [...prevState.tabs, newTab],
      activeTabId: newTab.id
    }), () => {
      if (autoDiscover && url) {
        this.discoverForTab(newTab.id, url)
      }
    })
  }

  private getActiveTab = (): DiscoveryTab | null => {
    return this.state.tabs.find(tab => tab.id === this.state.activeTabId) || null
  }

  private updateActiveTab = (updates: Partial<DiscoveryTab>) => {
    this.setState(prevState => ({
      tabs: prevState.tabs.map(tab => 
        tab.id === this.state.activeTabId 
          ? { ...tab, ...updates, hasUnsavedChanges: true }
          : tab
      )
    }))
  }

  private updateTabData = (field: keyof DiscoveryData, value: any) => {
    const activeTab = this.getActiveTab()
    if (!activeTab || !activeTab.data) return

    const updatedData = { ...activeTab.data, [field]: value }
    this.updateActiveTab({ data: updatedData })
  }

  private updateTabColors = (colorType: 'primary' | 'secondary', value: string) => {
    const activeTab = this.getActiveTab()
    if (!activeTab || !activeTab.data) return

    const updatedColors = { ...activeTab.data.colors, [colorType]: value }
    this.updateTabData('colors', updatedColors)
  }

  private discoverForTab = (tabId: string, url: string) => {
    this.setState(prevState => ({
      tabs: prevState.tabs.map(tab => 
        tab.id === tabId 
          ? { ...tab, isLoading: true, url, title: this.extractDomainFromUrl(url) }
          : tab
      ),
      error: ''
    }))

    AjaxWrapper
      .ajax(`${AjaxWrapper.getServerUrl()}/rex/${this.state.identifier}/discover`, HttpMethod.POST, {url})
      .then((response) => {
        // Extract data from both processed results and raw JINA analysis
        const processedData = response.results.data
        const jinaData = (response.results.raw && response.results.raw.jina && response.results.raw.jina.analyzed && response.results.raw.jina.analyzed.json) ? response.results.raw.jina.analyzed.json : {}
        const puppeteerData = (response.results.raw && response.results.raw.puppeteer) ? response.results.raw.puppeteer : {}
        
        // Use JINA colors if available, otherwise fall back to Puppeteer colors, then processed data
        const colors = {
          primary: (jinaData.elements && jinaData.elements.primary) || 
                   (puppeteerData.analyzed && puppeteerData.analyzed.json && puppeteerData.analyzed.json.primary) || 
                   (processedData.colors && processedData.colors.primary) || 
                   '#000000',
          secondary: (jinaData.elements && jinaData.elements.secondary) || 
                    (puppeteerData.analyzed && puppeteerData.analyzed.json && puppeteerData.analyzed.json.secondary) || 
                    (processedData.colors && processedData.colors.secondary) || 
                    '#ffffff'
        }
        
        // Use JINA image if available, otherwise processed data image
        const image = (jinaData.elements && jinaData.elements.image) || processedData.image || ''
        
        // Extract users from JINA data (note: it's 'user' not 'users' in the API)
        const users = jinaData.user || []
        
        this.setState(prevState => ({
          tabs: prevState.tabs.map(tab => 
            tab.id === tabId 
              ? { 
                  ...tab, 
                  data: {
                    image,
                    colors,
                    style: jinaData.style || processedData.style || '',
                    summary: jinaData.summary || processedData.summary || '',
                    users,
                    url,
                    title: processedData.title || this.extractDomainFromUrl(url)
                  },
                  rawData: {
                    account: response.results.raw.account || {},
                    puppeteer: response.results.raw.puppeteer || {},
                    jina: response.results.raw.jina || {}
                  },
                  isLoading: false,
                  hasUnsavedChanges: true
                }
              : tab
          )
        }))
      })
      .catch((error) => {
        this.setState(prevState => ({
          tabs: prevState.tabs.map(tab => 
            tab.id === tabId ? { ...tab, isLoading: false } : tab
          ),
          error: 'Failed to analyze website. Please check the URL and try again.'
        }))
      })
  }

  private handleUrlSubmit = () => {
    const activeTab = this.getActiveTab()
    if (!activeTab || !activeTab.url.trim()) return

    this.discoverForTab(activeTab.id, activeTab.url)
  }

  private saveTab = (tabId: string) => {
    const tab = this.state.tabs.find(t => t.id === tabId)
    if (!tab || !tab.data) return

    // Save image to gallery if it's a URL (not already in gallery)
    const savePromises = []
    
    if (tab.data.image && tab.data.image.startsWith('http') && !tab.data.image.includes('gallery')) {
      const saveImagePromise = AjaxWrapper
        .ajax(`${AjaxWrapper.getServerUrl()}/${this.state.identifier}/gallery/source`, HttpMethod.POST, {
          imageUrl: tab.data.image
        })
        .then((hostedImage) => {
          // Update the tab with the gallery image URL
          this.setState(prevState => ({
            tabs: prevState.tabs.map(t => 
              t.id === tabId && t.data 
                ? { ...t, data: { ...t.data, image: hostedImage.cdnUrl } }
                : t
            )
          }))
          return hostedImage
        })
        .catch((error) => {
          // Continue with save even if image save fails
        })
      
      savePromises.push(saveImagePromise)
    }

    // Save the discovery data
    const saveDataPromise = this.context.entityMetadata.getEntityObject('accountDiscovery')
      .then((entity) => {
        entity.setRecordId(this.state.identifier, this.state.accountId)
        entity.data.id = tabId
        entity.data.accountId = this.state.accountId
        entity.data.url = tab.data.url
        entity.data.title = tab.data.title
        entity.data.summary = tab.data.summary
        entity.data.colors = tab.data.colors
        entity.data.content = JSON.stringify({
          style: tab.data.style,
          title: tab.data.title,
          url: tab.data.url,
          summary: tab.data.summary,
          users: tab.data.users
        })
        entity.data.image = tab.data.image
        
        return entity.save()
      })

    savePromises.push(saveDataPromise)

    Promise.all(savePromises)
      .then(() => {
        this.setState(prevState => ({
          tabs: prevState.tabs.map(t => 
            t.id === tabId ? { ...t, hasUnsavedChanges: false } : t
          )
        }))
        // Show success flash message
        this.context.store.dispatch(Flash.showFlashMessage('Background discovery saved successfully!'))
      })
      .catch((error) => {
        // Show error flash message
        this.context.store.dispatch(Flash.showFlashError('Failed to save discovery. Please try again.'))
        this.setState({ error: 'Failed to save discovery. Please try again.' })
      })
  }

  private removeTab = (tabId: string) => {
    if (this.state.tabs.length === 1) {
      // Instead of removing the last tab, clear its content
      this.setState(prevState => ({
        tabs: prevState.tabs.map(tab => 
          tab.id === tabId 
            ? { ...tab, url: '', data: null, hasUnsavedChanges: false, title: 'New Discovery' }
            : tab
        )
      }))
      return
    }
    
    const newTabs = this.state.tabs.filter(tab => tab.id !== tabId)
    const newActiveTab = newTabs.length > 0 ? newTabs[0].id : ''
    
    this.setState({
      tabs: newTabs,
      activeTabId: newActiveTab
    })
  }


  private shouldShowTabNavigation = (): boolean => {
    // Only show tabs if we have multiple tabs
    return this.state.tabs.length > 1
  }

  private hasCompletedDiscovery = (): boolean => {
    // Check if we have at least one tab with completed data
    return this.state.tabs.some(tab => tab.data !== null)
  }

  private shouldShowActionButtons = (): boolean => {
    // Show action buttons when we have completed discoveries
    return this.hasCompletedDiscovery()
  }

  private openImageSelector = () => {
    this.setState({ showImageSelectorModal: true })
  }

  private closeImageSelector = (key: string) => {
    this.setState({ showImageSelectorModal: false })
  }

  private handleImageSelect = (selectedImage: { url: string; imageWidth: number; imageHeight: number }) => {
    const activeTab = this.getActiveTab()
    if (!activeTab || !activeTab.data) return

    // Update the image in the current tab
    this.updateTabData('image', selectedImage.url)
    // Close the modal
    this.setState({ showImageSelectorModal: false })
  }

  private toggleRawDataCard = (cardName: string) => {
    if (cardName === this.state.openRawDataCard) {
      this.setState({ openRawDataCard: '' })
    } else {
      this.setState({ openRawDataCard: cardName })
    }
  }

  private renderColorSwatch = (title: string, color: string) => {
    return (
      <div className="mb-3">
        <div className="small text-muted mb-1">{title}: {color}</div>
        <div
          style={{
            width: '100px',
            height: '60px',
            backgroundColor: color,
            border: '2px solid #dee2e6',
            borderRadius: '4px'
          }}
        />
      </div>
    )
  }

  private renderPuppeteerCard = (activeTab: DiscoveryTab) => {
    if (!activeTab.rawData || !activeTab.rawData.puppeteer) return null
    
    const { image } = activeTab.rawData.puppeteer.raw || {}
    const { primary = '', secondary = '' } = (activeTab.rawData.puppeteer.analyzed && activeTab.rawData.puppeteer.analyzed.json) ? activeTab.rawData.puppeteer.analyzed.json : {}
    
    if (!image && !primary && !secondary) return null
    
    const isOpen = this.state.openRawDataCard === 'puppeteer'
    
    return (
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Puppeteer Screenshot Analysis</h6>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => this.toggleRawDataCard('puppeteer')}
            >
              {isOpen ? 'Hide' : 'Show'}
            </Button>
          </div>
          {isOpen && (
            <div className="mt-3">
              <Row>
                {primary && (
                  <Col md={4}>
                    {this.renderColorSwatch('Primary Color', primary)}
                  </Col>
                )}
                {secondary && (
                  <Col md={4}>
                    {this.renderColorSwatch('Secondary Color', secondary)}
                  </Col>
                )}
              </Row>
              {image && (
                <div className="mt-3">
                  <h6>Screenshot</h6>
                  <img src={image} alt="Website screenshot" className="img-fluid" style={{ maxWidth: '100%', border: '1px solid #dee2e6', borderRadius: '4px' }} />
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  private renderJinaCard = (activeTab: DiscoveryTab) => {
    if (!activeTab.rawData || !activeTab.rawData.jina || !activeTab.rawData.jina.analyzed) return null
    
    const { summary, user, style, elements } = activeTab.rawData.jina.analyzed.json || {}
    
    if (!summary && !user && !style && !elements) return null
    
    const isOpen = this.state.openRawDataCard === 'jina'
    
    return (
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">JINA Content Analysis</h6>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => this.toggleRawDataCard('jina')}
            >
              {isOpen ? 'Hide' : 'Show'}
            </Button>
          </div>
          {isOpen && (
            <div className="mt-3">
              {summary && (
                <div className="mb-3">
                  <h6>Summary</h6>
                  <p className="text-muted">{summary}</p>
                </div>
              )}
              {style && (
                <div className="mb-3">
                  <h6>Writing Style</h6>
                  <p className="text-muted">{style}</p>
                </div>
              )}
              {user && user.length > 0 && (
                <div className="mb-3">
                  <h6>Target Audience</h6>
                  <ul className="text-muted">
                    {user.map((userType: string, index: number) => (
                      <li key={index}>{userType}</li>
                    ))}
                  </ul>
                </div>
              )}
              {elements && (
                <div className="mb-3">
                  <h6>Visual Elements</h6>
                  <Row>
                    {elements.primary && (
                      <Col md={4}>
                        {this.renderColorSwatch('Primary', elements.primary)}
                      </Col>
                    )}
                    {elements.secondary && (
                      <Col md={4}>
                        {this.renderColorSwatch('Secondary', elements.secondary)}
                      </Col>
                    )}
                  </Row>
                  {elements.image && (
                    <div className="mt-2">
                      <div className="small text-muted mb-1">Brand Image</div>
                      <img src={elements.image} alt="Brand logo" style={{ maxHeight: '100px', border: '1px solid #dee2e6', borderRadius: '4px' }} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  private renderJinaRawCard = (activeTab: DiscoveryTab) => {
    if (!activeTab.rawData || !activeTab.rawData.jina || !activeTab.rawData.jina.raw) return null
    
    const { text } = activeTab.rawData.jina.raw || {}
    
    if (!text) return null
    
    const isOpen = this.state.openRawDataCard === 'jina_raw'
    
    return (
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Raw JINA Markdown Content</h6>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => this.toggleRawDataCard('jina_raw')}
            >
              {isOpen ? 'Hide' : 'Show'}
            </Button>
          </div>
          {isOpen && (
            <div className="mt-3">
              <div style={{ 
                maxHeight: '400px', 
                overflow: 'auto', 
                backgroundColor: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '4px',
                fontSize: '14px',
                whiteSpace: 'pre-wrap'
              }}>
                {text}
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  private renderJinaJsonCard = (activeTab: DiscoveryTab) => {
    if (!activeTab.rawData || !activeTab.rawData.jina || !activeTab.rawData.jina.analyzed) return null
    
    const jsonData = activeTab.rawData.jina.analyzed.json
    
    if (!jsonData) return null
    
    const isOpen = this.state.openRawDataCard === 'jina_json'
    
    return (
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">JINA JSON Breakdown</h6>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => this.toggleRawDataCard('jina_json')}
            >
              {isOpen ? 'Hide' : 'Show'}
            </Button>
          </div>
          {isOpen && (
            <div className="mt-3">
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                wordWrap: 'break-word',
                backgroundColor: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '4px',
                fontSize: '13px',
                maxHeight: '400px',
                overflow: 'auto'
              }}>
                {JSON.stringify(jsonData, null, 2)}
              </pre>
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  private renderAccountCard = (activeTab: DiscoveryTab) => {
    if (!activeTab.rawData || !activeTab.rawData.account) return null
    
    const accountData = activeTab.rawData.account
    
    const isOpen = this.state.openRawDataCard === 'account'
    
    return (
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Account Information</h6>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => this.toggleRawDataCard('account')}
            >
              {isOpen ? 'Hide' : 'Show'}
            </Button>
          </div>
          {isOpen && (
            <div className="mt-3">
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                wordWrap: 'break-word',
                backgroundColor: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '4px',
                fontSize: '13px',
                maxHeight: '400px',
                overflow: 'auto'
              }}>
                {JSON.stringify(accountData, null, 2)}
              </pre>
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  public render(): React.ReactNode {
    const activeTab = this.getActiveTab()
    const showTabNavigation = this.shouldShowTabNavigation()
    const showActionButtons = this.shouldShowActionButtons()

    return (
      <div className="background-discovery">
        <HeaderComponent
          title="AI Background"
          subTitle="Discovery"
          description={["Analyze websites to extract branding, style, and content insights."]}
          icon="fas fa-search"
        />

        {this.state.error && (
          <Alert color="danger" className="mb-3">
            {this.state.error}
          </Alert>
        )}

        {/* Tab Navigation - Only show when we have multiple tabs */}
        {showTabNavigation && (
          <Nav tabs className="discovery-tabs mb-3">
            {this.state.tabs.map((tab) => (
              <NavItem key={tab.id}>
                <NavLink
                  className={this.state.activeTabId === tab.id ? 'active' : ''}
                  onClick={() => this.setState({ activeTabId: tab.id })}
                >
                  <span>
                    {tab.isLoading && '⏳ '}
                    {tab.hasUnsavedChanges && '• '}
                    {tab.title}
                  </span>
                  {this.state.tabs.length > 1 && (
                    <button
                      className="tab-close-btn"
                      onClick={(e) => {
                        e.stopPropagation()
                        this.removeTab(tab.id)
                      }}
                    >
                      ×
                    </button>
                  )}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        )}

        {/* Show helpful message when starting fresh and no analysis has been started */}
        {!showTabNavigation && !this.hasCompletedDiscovery() && !this.state.tabs.some(tab => tab.isLoading) && (
          <div className="text-center text-muted mb-3">
            <p>Enter a website URL below to start your first website analysis.</p>
          </div>
        )}

        {/* URL Section - Input for new analysis, display for completed analysis */}
        <Card className="mb-3">
          <CardBody>
            {activeTab && activeTab.data ? (
              // Show URL as read-only label when analysis is complete
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>Analyzed Website</Label>
                    <div className="analyzed-url-display">
                      <div className="d-flex align-items-center">
                        <div className="analyzed-url-text">
                          <strong>
                            <i className="fas fa-globe me-2"></i>
                            {activeTab.url || activeTab.data.title || 'Website Analysis'}
                          </strong>
                        </div>
                        <div className="ms-auto">
                          <Button
                            color="outline-primary"
                            size="sm"
                            onClick={() => this.createNewTab()}
                          >
                            + New Analysis
                          </Button>
                        </div>
                      </div>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            ) : (
              // Show URL input for new analysis
              <Row>
                <Col md={8}>
                  <FormGroup>
                    <Label>Website URL</Label>
                    <Input
                      type="url"
                      placeholder="https://example.com"
                      value={activeTab ? activeTab.url : ''}
                      onChange={(e) => {
                        if (activeTab) {
                          this.updateActiveTab({ url: e.target.value })
                        } else {
                          // Create a new tab if none exists
                          this.createNewTab(e.target.value)
                        }
                      }}
                      disabled={activeTab ? activeTab.isLoading : false}
                    />
                  </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <FormGroup className="w-100">
                    <Button
                      color="primary"
                      onClick={() => {
                        if (!activeTab) {
                          // Create a new tab first if none exists
                          this.createNewTab()
                        } else {
                          this.handleUrlSubmit()
                        }
                      }}
                      disabled={activeTab && (!activeTab.url.trim() || activeTab.isLoading)}
                      block
                    >
                      {activeTab && activeTab.isLoading ? 'Analyzing...' : 'Analyze Website'}
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>

        {/* Loading State */}
        {activeTab && activeTab.isLoading && <Loading size="64" />}

        {/* Discovery Results */}
        {activeTab && activeTab.data && !activeTab.isLoading && (
          <div className="discovery-results">
            {/* Visual Elements */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-3">Visual Identity</h5>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Primary Image</Label>
                      <div className="image-with-overlay mb-2">
                        <img 
                          src={activeTab.data.image} 
                          alt="Website banner"
                          className="img-fluid"
                          style={{ maxHeight: '200px', width: '100%', objectFit: 'cover' }}
                        />
                        <div className="image-overlay-icons">
                          <div 
                            className="overlay-icon edit-icon"
                            onClick={this.openImageSelector}
                            title="Change Image"
                          >
                            <i className="fas fa-camera" />
                          </div>
                        </div>
                      </div>
                      <Input
                        type="url"
                        value={activeTab.data.image}
                        onChange={(e) => this.updateTabData('image', e.target.value)}
                        placeholder="Image URL"
                        bsSize="sm"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Primary Color</Label>
                      <div className="color-picker-group">
                        <div
                          className="color-preview"
                          style={{ backgroundColor: activeTab.data.colors.primary }}
                        />
                        <Input
                          type="color"
                          value={activeTab.data.colors.primary}
                          onChange={(e) => this.updateTabColors('primary', e.target.value)}
                        />
                        <Input
                          type="text"
                          value={activeTab.data.colors.primary}
                          onChange={(e) => this.updateTabColors('primary', e.target.value)}
                          placeholder="#000000"
                        />
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Secondary Color</Label>
                      <div className="color-picker-group">
                        <div
                          className="color-preview"
                          style={{ backgroundColor: activeTab.data.colors.secondary }}
                        />
                        <Input
                          type="color"
                          value={activeTab.data.colors.secondary}
                          onChange={(e) => this.updateTabColors('secondary', e.target.value)}
                        />
                        <Input
                          type="text"
                          value={activeTab.data.colors.secondary}
                          onChange={(e) => this.updateTabColors('secondary', e.target.value)}
                          placeholder="#000000"
                        />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Content Information */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-3">Content & Style</h5>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Website Title</Label>
                      <Input
                        type="text"
                        value={activeTab.data.title}
                        onChange={(e) => this.updateTabData('title', e.target.value)}
                        placeholder="Website title"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Style Description</Label>
                      <Input
                        type="textarea"
                        rows={3}
                        value={activeTab.data.style}
                        onChange={(e) => this.updateTabData('style', e.target.value)}
                        placeholder="Describe the website's visual style"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label>Summary</Label>
                      <Input
                        type="textarea"
                        rows={4}
                        value={activeTab.data.summary}
                        onChange={(e) => this.updateTabData('summary', e.target.value)}
                        placeholder="Website summary and key information"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {activeTab && activeTab.data && activeTab.data.users && activeTab.data.users.length > 0 && (
                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label>Target Audience</Label>
                        <Input
                          type="textarea"
                          rows={2}
                          value={activeTab.data.users.join(', ')}
                          onChange={(e) => this.updateTabData('users', e.target.value.split(', ').filter(u => u.trim()))}
                          placeholder="Target audience demographics"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>

            {/* Raw Analysis Data - Detailed breakdown */}
            {activeTab && activeTab.rawData && (activeTab.rawData.jina || activeTab.rawData.puppeteer) && (
              <div className="raw-analysis-section mt-4">
                <h5 className="mb-3">Detailed Analysis Data</h5>
                
                {/* JINA Content Analysis Card */}
                {this.renderJinaCard(activeTab)}
                
                {/* Puppeteer Screenshot Analysis Card */}
                {this.renderPuppeteerCard(activeTab)}
              
                {/* Raw JINA Markdown Content Card */}
                {this.renderJinaRawCard(activeTab)}
                
                {/* JINA JSON Breakdown Card */}
                {this.renderJinaJsonCard(activeTab)}
                
                {/* Account Information Card */}
                {this.renderAccountCard(activeTab)}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons - Only show save button when there are unsaved changes */}
        {showActionButtons && (
          <div className="discovery-actions d-flex justify-content-end">
            {activeTab && activeTab.data && (
              <Button
                color="success"
                onClick={() => this.saveTab(activeTab.id)}
                disabled={!activeTab.hasUnsavedChanges}
              >
                {activeTab.hasUnsavedChanges ? 'Save Changes' : 'Saved'}
              </Button>
            )}
          </div>
        )}

        {/* Image Selector Modal */}
        {this.state.showImageSelectorModal && (
          <ImageSelectorModal
            data={this.state.showImageSelectorModal ? {} : null}
            closeModal={this.closeImageSelector}
            onSelectImage={this.handleImageSelect}
            saveToGallery={true}
            allowGallery={false}
            allowUpload={true}
            allowURL={true}
            allowAIGeneration={false}
            contentType="article"
          />
        )}

      </div>
    )
  }
}